import { useCallback, useEffect, useRef, useState } from 'react';

type ActiveHighlightType = 'hash' | 'click';

interface ActiveHighlight {
  type: ActiveHighlightType;
  elementId: string;
}

/**
 * Centralized hook for managing active highlights (yellow boxes).
 * Handles both URL hash highlights and user click selections by adding CSS classes.
 * This allows elements to have both active highlight and hover outline simultaneously.
 *
 * @param activeHighlightClassName - The CSS Module class name for active highlights (passed from component)
 */
export function useActiveHighlight(activeHighlightClassName: string) {
  // Store highlights as a Map: elementId -> ActiveHighlight
  const [highlights, setHighlights] = useState<Map<string, ActiveHighlight>>(
    new Map()
  );
  const highlightsRef = useRef(highlights);

  // Keep ref in sync with state for use in effects
  useEffect(() => {
    highlightsRef.current = highlights;
  }, [highlights]);

  /**
   * Adds an active highlight to an element by adding the CSS class
   */
  const addActiveHighlight = useCallback(
    (elementId: string, type: ActiveHighlightType) => {
      // Check if already highlighted using ref
      if (highlightsRef.current.has(elementId)) {
        return;
      }

      // Try to find element - handle special characters in IDs
      let element = document.getElementById(elementId);

      // Fallback: try with CSS.escape for special characters (e.g., dots)
      if (!element) {
        const escapedSelector = CSS.escape(elementId);
        const elements = document.querySelectorAll(`#${escapedSelector}`);
        if (elements.length > 0) {
          element = elements[0] as HTMLElement;
        }
      }

      if (!element) {
        console.warn(
          `[useActiveHighlight] Element with id "${elementId}" not found`
        );
        return;
      }

      // Add the activeHighlight CSS Module class
      // Both classes can coexist - CSS handles the combined state
      element.classList.add(activeHighlightClassName);

      // Only scroll for hash navigation (URL fragment), not for click selection
      // Click selection should behave like hover - just highlight in place
      if (type === 'hash') {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      // Update state
      setHighlights((prev) => {
        const newMap = new Map(prev);
        newMap.set(elementId, { type, elementId });
        return newMap;
      });
    },
    [activeHighlightClassName]
  );

  /**
   * Removes an active highlight from an element
   */
  const removeActiveHighlight = useCallback(
    (elementId: string) => {
      const highlight = highlightsRef.current.get(elementId);
      if (!highlight) {
        return;
      }

      // Find element and remove the activeHighlight class
      let element = document.getElementById(elementId);

      // Fallback: try with CSS.escape for special characters
      if (!element) {
        const escapedSelector = CSS.escape(elementId);
        const elements = document.querySelectorAll(`#${escapedSelector}`);
        if (elements.length > 0) {
          element = elements[0] as HTMLElement;
        }
      }

      if (element) {
        // Remove CSS Module class - that's it!
        element.classList.remove(activeHighlightClassName);
      }

      // Update state
      setHighlights((prev) => {
        const newMap = new Map(prev);
        newMap.delete(elementId);
        return newMap;
      });
    },
    [activeHighlightClassName]
  );

  /**
   * Clears all active highlights
   */
  const clearAllHighlights = useCallback(() => {
    highlightsRef.current.forEach((highlight) => {
      let element = document.getElementById(highlight.elementId);

      // Fallback: try with CSS.escape for special characters
      if (!element) {
        const escapedSelector = CSS.escape(highlight.elementId);
        const elements = document.querySelectorAll(`#${escapedSelector}`);
        if (elements.length > 0) {
          element = elements[0] as HTMLElement;
        }
      }

      if (element) {
        // Remove CSS Module class - that's it!
        element.classList.remove(activeHighlightClassName);
      }
    });

    setHighlights(new Map());
  }, [activeHighlightClassName]);

  /**
   * Toggles a click highlight (add if not present, remove if present)
   */
  const toggleActiveHighlight = useCallback(
    (elementId: string) => {
      if (highlightsRef.current.has(elementId)) {
        removeActiveHighlight(elementId);
      } else {
        addActiveHighlight(elementId, 'click');
      }
    },
    [addActiveHighlight, removeActiveHighlight]
  );

  /**
   * Check if an element is highlighted
   */
  const isHighlighted = useCallback((elementId: string) => {
    return highlightsRef.current.has(elementId);
  }, []);

  /**
   * Get highlight info for an element
   */
  const getHighlight = useCallback((elementId: string) => {
    return highlightsRef.current.get(elementId);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up any remaining highlights
      highlightsRef.current.forEach((highlight) => {
        let element = document.getElementById(highlight.elementId);

        // Fallback: try with CSS.escape for special characters
        if (!element) {
          const escapedSelector = CSS.escape(highlight.elementId);
          const elements = document.querySelectorAll(`#${escapedSelector}`);
          if (elements.length > 0) {
            element = elements[0] as HTMLElement;
          }
        }

        if (element) {
          // Remove CSS Module class - that's it!
          element.classList.remove(activeHighlightClassName);
        }
      });
    };
  }, [activeHighlightClassName]);

  return {
    highlights,
    addActiveHighlight,
    removeActiveHighlight,
    clearAllHighlights,
    toggleActiveHighlight,
    isHighlighted,
    getHighlight,
  };
}
